import { useMemo } from "react";

import { useTranslation } from "next-i18next";

import { GetProductShippingDetailsQuery_getProductShippingDetailsV2_ProductShippingDetailsV2_shippingOptions_ProductShippingOptionV2 } from "@/Apollo/schema";

/**
 * Builds the delivery-estimate label (e.g. "3 days", "3-7 days") for a shipping
 * option, or `undefined` when no estimate is available.
 */
export const useProductDeliveryEstimateLabel = (
  option: GetProductShippingDetailsQuery_getProductShippingDetailsV2_ProductShippingDetailsV2_shippingOptions_ProductShippingOptionV2
) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const minDays = option.deliveryEstimate?.minDays;
    const maxDays = option.deliveryEstimate?.maxDays;

    // Only an upper bound → "Within X days".
    if (!minDays && maxDays) {
      // ponytail: placeholder key for new copy "Within {{0}} days"
      return t("pdp:pdp-usp-shipping-line2-within-days", { 0: maxDays });
    }

    const daysArray = [minDays, maxDays].filter(Boolean);

    if (
      daysArray.length === 1 ||
      (daysArray.length === 2 && daysArray[0] === daysArray[1])
    ) {
      const estimate = daysArray[0];

      return estimate === 1
        ? t("pdp:pdp-usp-shipping-line2-days.one", { 0: daysArray[0] })
        : t("pdp:pdp-usp-shipping-line2-days.other", { 0: daysArray[0] });
    }

    if (daysArray.length === 2) {
      return t("pdp:pdp-usp-shipping-line2-to-days", {
        0: daysArray[0],
        1: daysArray[1],
      });
    }
  }, [option.deliveryEstimate?.maxDays, option.deliveryEstimate?.minDays, t]);
};
